"use client";

import { useEffect } from "react";
import { trackScrollDepth } from "../../lib/utils/analytics";

interface ScrollDepthTrackerProps {
  // Slug or stable identifier for the page being tracked.
  page: string;
}

const THRESHOLDS = [25, 50, 75, 100] as const;

// Fires trackScrollDepth at 25/50/75/100% of the document scroll, once each
// per page-load. Designed to be dropped into long-form pages (guides) so we
// can tell whether the prose actually gets read past the fold.
export default function ScrollDepthTracker({ page }: ScrollDepthTrackerProps) {
  useEffect(() => {
    const fired = new Set<number>();

    const handler = () => {
      const doc = document.documentElement;
      const scrollTop = window.scrollY || doc.scrollTop;
      const viewport = window.innerHeight;
      const total = doc.scrollHeight - viewport;
      if (total <= 0) return;
      const pct = (scrollTop / total) * 100;
      for (const t of THRESHOLDS) {
        if (pct >= t && !fired.has(t)) {
          fired.add(t);
          trackScrollDepth(t, page);
        }
      }
    };

    // Run once in case the page is already past a threshold (short content,
    // browser back-button to mid-scroll position).
    handler();

    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, [page]);

  return null;
}
